import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { UserResponse } from '@clarity/contract';
import {
  useClient,
  useWorkspaceUsers,
  useAddWorkspaceUser,
  useRemoveWorkspaceUser,
} from '../queries/index.js';
import { useSession } from '../lib/session.js';
import { useClientContext } from '../lib/clientContext.js';
import { QueryState } from './QueryState.js';
import { errorStatus, shortDate } from './format.js';
import { IconClients } from '../components/icons.js';

/**
 * Workspace users (clarity-workspace-users). A workspace (= client) may have
 * many user logins; this surface lists them and lets you add a teammate or
 * remove one. It serves BOTH mounts:
 * - ADMIN at `/clients/:clientId/users` (the client id is the route param), and
 * - a CLIENT (owner) at `/c/:clientId/users` (self-serve, own workspace).
 *
 * The server authorizes either way (admin → any workspace; client → only its
 * own). Created users are always `role: 'client'` bound to the workspace; there
 * is no reset flow yet, so the creator shares the initial password. A caller
 * cannot remove themselves, and the last user cannot be removed.
 *
 * `clientId` resolution: the route param when present (admin page), else the
 * active client in scope (a client inside its `/c/:clientId` workspace).
 */
export function WorkspaceUsers() {
  const { clientId: routeClientId } = useParams();
  const { activeClientId, selectedClient } = useClientContext();
  const clientId = routeClientId ?? activeClientId ?? '';
  const { role } = useSession();
  const isAdmin = role === 'admin';

  const client = useClient(clientId, Boolean(clientId) && isAdmin);
  // A client-role user can't read the clients list; fall back to the context name.
  const clientName =
    (client.data?.status === 200 ? client.data.body.name : null) ?? selectedClient?.name ?? null;

  return (
    <section className="billing">
      <div className="page-head billing-head">
        <div>
          {isAdmin && (
            <Link to="/clients" className="billing-back">
              ← Clients
            </Link>
          )}
          <h2>{clientName ? `${clientName} — users` : 'Workspace users'}</h2>
          <p className="sub">
            Everyone here can sign in to this workspace. They share the same data and the same
            monthly AI budget; each person gets their own private advisor chat.
          </p>
        </div>
      </div>

      {clientId ? (
        <WorkspaceUsersPanel key={clientId} clientId={clientId} />
      ) : (
        <div className="card empty">
          <IconClients className="empty__glyph" />
          <p>No workspace in scope.</p>
        </div>
      )}
    </section>
  );
}

function WorkspaceUsersPanel({ clientId }: { clientId: string }) {
  const { session } = useSession();
  const myUserId = session?.user.id ?? null;
  const list = useWorkspaceUsers(clientId);
  const rows: UserResponse[] = list.data?.status === 200 ? list.data.body.users : [];

  return (
    <div className="card subscription-card">
      <h3>Users</h3>
      <QueryState isLoading={list.isLoading} isError={list.isError} label="users">
        <ul className="list" aria-label="Workspace users">
          {rows.map((u) => (
            <UserRow
              key={u.id}
              clientId={clientId}
              user={u}
              isMe={u.id === myUserId}
              canRemove={rows.length > 1 && u.id !== myUserId}
            />
          ))}
        </ul>
      </QueryState>

      <AddUserForm clientId={clientId} />
    </div>
  );
}

/** One user row: identity + a remove action (disabled for self / the last user). */
function UserRow({
  clientId,
  user,
  isMe,
  canRemove,
}: {
  clientId: string;
  user: UserResponse;
  isMe: boolean;
  canRemove: boolean;
}) {
  const remove = useRemoveWorkspaceUser(clientId);
  return (
    <li>
      <div>
        <strong className="num">{user.phone ?? user.email ?? user.id.slice(0, 8)}</strong>
        {isMe && <span className="muted"> (you)</span>}
        {user.phoneVerifiedAt === null && <span className="muted"> · unverified</span>}
        <span className="muted num"> · added {shortDate(user.createdAt)}</span>
      </div>
      <span className="actions">
        <button
          type="button"
          className="sm ghost"
          onClick={() => remove.mutate({ params: { clientId, userId: user.id } })}
          disabled={!canRemove || remove.isPending}
          title={isMe ? 'You cannot remove your own account' : undefined}
        >
          {remove.isPending ? 'Removing…' : 'Remove'}
        </button>
      </span>
      {remove.isError && (
        <p role="alert" className="error">
          Could not remove this user.
        </p>
      )}
    </li>
  );
}

/** E.164: '+' then 1–15 digits. */
const E164 = /^\+[1-9]\d{0,14}$/;

/** Add a teammate: phone (E.164) + initial password (8–128 chars). */
function AddUserForm({ clientId }: { clientId: string }) {
  const add = useAddWorkspaceUser(clientId);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');

  const phoneTrim = phone.trim();
  const phoneInvalid = phoneTrim !== '' && !E164.test(phoneTrim);
  const passwordInvalid = password !== '' && (password.length < 8 || password.length > 128);
  const canSubmit =
    phoneTrim !== '' && !phoneInvalid && password.length >= 8 && !passwordInvalid && !add.isPending;

  const conflict = add.isError && errorStatus(add.error) === 409;
  const saved = add.data?.status === 201 && !add.isPending;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    add.mutate(
      { params: { clientId }, body: { phone: phoneTrim, password } },
      {
        onSuccess: (res) => {
          if (res.status === 201) {
            setPhone('');
            setPassword('');
          }
        },
      },
    );
  }

  return (
    <form className="subscription-field" onSubmit={onSubmit} aria-label="Add a user">
      <h4>Add a user</h4>
      <div className="subscription-field__inline">
        <label htmlFor="wu-phone">
          Phone
          <input
            id="wu-phone"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+962790000000"
            aria-invalid={phoneInvalid || undefined}
          />
        </label>
        <label htmlFor="wu-password">
          Initial password
          <input
            id="wu-password"
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="at least 8 characters"
            aria-invalid={passwordInvalid || undefined}
          />
        </label>
      </div>
      <div className="subscription-field__actions">
        <button type="submit" disabled={!canSubmit}>
          {add.isPending ? 'Adding…' : 'Add user'}
        </button>
      </div>
      <p className="muted">
        The new user signs in with this phone number. They get a WhatsApp code to verify it the
        first time; until then their account shows as unverified. Forgotten passwords are reset by
        phone, so you don&apos;t need to share this one long-term.
      </p>
      {(phoneInvalid || passwordInvalid) && (
        <p role="alert" className="error">
          {phoneInvalid
            ? 'Enter a valid phone in E.164 form (e.g. +962790000000).'
            : 'Password must be 8–128 characters.'}
        </p>
      )}
      {add.isError && (
        <p role="alert" className="error">
          {conflict
            ? 'A user with this phone already exists. Your entries are unchanged.'
            : 'Could not add the user. Your entries are unchanged — try again.'}
        </p>
      )}
      {saved && (
        <p role="status" className="muted">
          User added.
        </p>
      )}
    </form>
  );
}
